import React from 'react';
import {Section, Table, NameCell, ValueCell, InputCell} from '../components/cells';
import {money, money2, percent} from '../model/format';

/**
 * Expenses sheet. Real Estate Taxes come from Property Information (appraised / new assessed
 * value x mill rate) and the Management Fee is a share of EGI; every other line is entered.
 */
export default function Expenses({inputs, calc, setField}) {
    const input = (key, props = {}) => <InputCell value={inputs[key]} onChange={value => setField(key, value)} {...props}/>;
    const expenses = calc.expenses;
    const vacancyRow = (key, label, figures, className = '') => (
        <tr key={key} className={className}>
            <NameCell className={key === 'vacancy' || key === 'economic' ? '' : 'mf-indent'}>{label}</NameCell>
            {key === 'economic' ? <ValueCell>{money(figures.current)}</ValueCell> : input(key, {unit: '$'})}
            {key === 'economic' ? <ValueCell>{money(figures.proforma)}</ValueCell> : input(`proforma_${key}`, {unit: '$'})}
            <ValueCell>{money(figures.current_per_unit)}</ValueCell>
            <ValueCell>{percent(figures.current_share)}</ValueCell>
            <ValueCell>{percent(figures.proforma_share)}</ValueCell>
        </tr>
    );

    return (
        <div className='mf-tab mf-expenses'>
            {/* Two equal halves split by the dashed divider; the two boxes stretch to the same height. */}
            <div className='Financials_list_columns mf-split mf-split--equal'>
                <div className='Financials_list_column mf-split__half'>
                    <Section title='Expenses'>
                        <Table className='mf-grid-table mf-expense-basis-table' header={['', 'Current', 'Proforma']}>
                            <tr>
                                <NameCell>Management Fee % (of EGI)</NameCell>
                                {input('management_fee_percent', {unit: '%'})}
                                {input('proforma_management_fee_percent', {unit: '%'})}
                            </tr>
                            <tr>
                                <NameCell>Effective Gross Income</NameCell>
                                <ValueCell>{money(expenses.egi.current)}</ValueCell>
                                <ValueCell>{money(expenses.egi.proforma)}</ValueCell>
                            </tr>
                        </Table>

                        <Table className='mf-grid-table mf-expenses-table'
                            header={['Expenses', 'Current (Annual)', 'Proforma (Annual)', 'Current Per Unit', 'Current PSF']}
                            footer={
                                <tr className='mf-total-row'>
                                    <NameCell>TOTAL EXPENSES</NameCell>
                                    <ValueCell>{money(expenses.total.current)}</ValueCell>
                                    <ValueCell>{money(expenses.total.proforma)}</ValueCell>
                                    <ValueCell>{money(expenses.total.current_per_unit)}</ValueCell>
                                    <ValueCell>{money2(expenses.total.current_psf)}</ValueCell>
                                </tr>
                            }
                        >
                            {expenses.lines.map(line => {
                                const calculated = line.key === 'real_estate_taxes' || line.key === 'management_fee';
                                return (
                                    <tr key={line.key}>
                                        <NameCell>{line.label}</NameCell>
                                        {calculated ? <ValueCell>{money(line.current)}</ValueCell> : input(line.key, {unit: '$'})}
                                        {calculated ? <ValueCell>{money(line.proforma)}</ValueCell> : input(`proforma_${line.key}`, {unit: '$'})}
                                        <ValueCell>{money(line.current_per_unit)}</ValueCell>
                                        <ValueCell>{money2(line.current_psf)}</ValueCell>
                                    </tr>
                                );
                            })}
                        </Table>
                    </Section>
                </div>
                <div className='Financials_list_column mf-split__half'>
                    <Section title='Vacancy' description='Enter as positive annual $'>
                        <Table className='mf-grid-table mf-vacancy-table'
                            header={['', 'Current (Annual)', 'Proforma (Annual)', 'Current Per Unit', 'Current % of GPR', 'Proforma % of GPR']}>
                            {vacancyRow('vacancy', 'Physical Vacancy', expenses.vacancy)}
                            {expenses.economic.map(item => vacancyRow(item.key, item.label, item))}
                            {vacancyRow('economic', 'Economic Vacancy (NRU + LTL + RC + BD)', expenses.economicTotal, 'mf-total-row')}
                        </Table>
                    </Section>
                </div>
            </div>

            <Section title='Expense Notes'>
                <textarea
                    className='mf-notes'
                    value={inputs.expense_notes || ''}
                    onChange={event => setField('expense_notes', event.currentTarget.value)}
                    rows={5}
                />
            </Section>
        </div>
    );
}
